<template>
  <v-card class="pt-4 px-4">
    <v-skeleton-loader
      v-if="!selectedReport"
      class="px-0 mt-2"
      :loading="true"
      type="text"
    />
    <v-card-title
      v-else
      class="subtitle-2"
    >
      <span>Share &quot;<span>{{ selectedReport.reportLabel }}</span>&quot;</span>
    </v-card-title>

    <v-card-text>
      <v-form ref="formShare">
        <v-row>
          <v-col>
            <v-row
              v-if="!isProtectedReport"
              class="mt-0"
            >
              <v-col class="col-grow">
                <v-combobox
                  v-model="sharedWith"
                  autocomplete="off"
                  dense
                  :disabled="!selectedReport || !selectedReport.canShareHard"
                  hide-details
                  :items="sharedWithInputOptions"
                  label="Add email or choose group"
                  name="sharedWith"
                  outlined
                  :return-object="false"
                  :rules="rulesSharedWith"
                  title="Enter an email or choose a group from the dropdown."
                  @keyup="revealShareFormInputs"
                  @update:search-input="revealShareFormInputs"
                />
              </v-col>
            </v-row>

            <v-expand-transition>
              <v-sheet
                v-if="!isProtectedReport && showShareFormInputs"
              >
                <v-row
                  class="mt-0"
                  transition="slide-y-transition"
                >
                  <v-col class="col-shrink">
                    <v-select
                      v-model="sharedRole"
                      autocomplete="off"
                      dense
                      hide-details
                      :items="roleOptions"
                      label="Role"
                      :menu-props="{ offsetY: true }"
                      name="sharedRole"
                      outlined
                      :rules="rulesRequired(selectedReport)"
                      title="Select the role of the user you want to share this report with."
                    />
                  </v-col>
                </v-row>

                <v-row
                  class="mt-0"
                  transition="slide-y-transition"
                >
                  <v-spacer />

                  <v-col class="col-auto">
                    <v-btn
                      :color="highlightColor"
                      small
                      text
                      @click.stop="clearShareFormInputs"
                    >
                      <span :class="`${secondaryColor}--text`">Cancel</span>
                    </v-btn>

                    <v-btn
                      color="primary"
                      class="elevation-0"
                      small
                      :disabled="!selectedReport || !selectedReport.canShareHard || !sharedWith"
                      @click.stop="shareReport(selectedReport)"
                    >
                      <span>Save</span>
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row class="mt-0">
                  <v-col>
                    <v-divider />
                  </v-col>
                </v-row>
              </v-sheet>
            </v-expand-transition>

            <v-sheet>
              <v-row v-if="isProtectedReport">
                <v-col>
                  <span>This is a template available to all {{ selectedReport.groupKey === 'team' ? 'network' : 'iAdOps' }} users. Simply copy the link to share it with its intended audience.</span>
                </v-col>
              </v-row>
            </v-sheet>

            <v-sheet v-if="selectedReport && !isProtectedReport">
              <v-row
                :class="`mt-0${!selectedReportRoles ? ' pb-4' : ''}`"
              >
                <v-col class="pt-2">
                  <v-expand-transition>
                    <v-row
                      v-if="!selectedReportRoles || selectedReportRoles.length === 0"
                      class="mt-2 px-4 pb-4"
                    >
                      No shares yet.
                    </v-row>

                    <v-list
                      v-else
                      class="pt-1 pb-0"
                      dense
                    >
                      <v-list-item
                        v-for="(sharedUser, index) in visibleRoles"
                        :key="index"
                        outlined
                        class="px-0"
                      >
                        <v-list-item-content>
                          <v-row class="align-center">
                            <v-col class="col-grow text-truncate ml-1">
                              <v-list-item-title class="subtitle-2">
                                <span>{{ sharedUser.userId }}</span>
                              </v-list-item-title>
                            </v-col>

                            <v-col class="col-auto">
                              <v-menu
                                bottom
                                offset-y
                                transition="slide-y-transition"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-btn
                                    class="elevation-0"
                                    outlined
                                    small
                                    v-on="on"
                                  >
                                    <span
                                      v-if="!sharedUser.isRevoked"
                                      class="pr-2"
                                    >{{ sharedUser.role }}</span>
                                    <span
                                      v-else
                                      class="pr-2"
                                    >Revoked</span>
                                    <v-icon
                                      small
                                    >
                                      arrow_drop_down
                                    </v-icon>
                                  </v-btn>
                                </template>

                                <v-list>
                                  <v-list-item
                                    v-for="(role, roleIndex) in roleOptions"
                                    :key="roleIndex"
                                    :disabled="role.value === sharedUser.role || role.disabled || !selectedReport.canShareHard"
                                    @click.stop="updateSharedUserRole(selectedReport, sharedUser, role.value)"
                                  >
                                    <v-list-item-title class="subtitle-2">
                                      <v-icon
                                        x-small
                                        class="pr-2"
                                        :color="(role.value === sharedUser.role && !sharedUser.isRevoked && 'grassgreen') || ((role.value === sharedUser.role || role.disabled || sharedUser.role === 'viewer') && 'grey') || ''"
                                      >
                                        {{ role.value === sharedUser.role && !sharedUser.isRevoked ? 'check' : 'radio_button_unchecked' }}
                                      </v-icon>
                                      <span>{{ role.text }}</span>
                                    </v-list-item-title>
                                  </v-list-item>

                                  <v-divider />

                                  <v-list-item
                                    v-if="!sharedUser.isRevoked"
                                    @click.stop="revokeAccessToReport(selectedReport, sharedUser)"
                                  >
                                    <v-list-item-title class="subtitle-2">
                                      <span>Revoke access</span>
                                    </v-list-item-title>
                                  </v-list-item>

                                  <v-list-item
                                    v-else
                                    @click.stop="restoreAccessToReport(selectedReport, sharedUser)"
                                  >
                                    <v-list-item-title class="subtitle-2">
                                      <span>Restore access</span>
                                    </v-list-item-title>
                                  </v-list-item>

                                  <v-list-item
                                    v-if="hasOwnerRights"
                                    @click.stop="softDeleteAccessToReport(selectedReport, sharedUser)"
                                  >
                                    <v-list-item-title class="subtitle-2">
                                      <span>Delete</span>
                                    </v-list-item-title>
                                  </v-list-item>

                                  <v-list-item
                                    v-if="inDevMode"
                                    title="Dev only"
                                    @click.stop="hardDeleteAccessToReport(selectedReport, sharedUser)"
                                  >
                                    <v-list-item-title class="subtitle-2 toasted--text">
                                      <span>Delete forever</span>
                                    </v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </v-col>
                          </v-row>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expand-transition>
                </v-col>
              </v-row>
            </v-sheet>

            <v-row class="mt-0">
              <v-col>
                <v-divider />
              </v-col>
            </v-row>

            <v-row class="pa-4 mt-0">
              <v-btn
                outlined
                color="primary"
                @click.stop="copyLinkToClipboard"
              >
                Copy Link
              </v-btn>

              <v-spacer />

              <v-btn
                color="primary"
                @click.stop="toggleModal('Share')"
              >
                Done
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { createReportRole } from '@/report/store/utils/report'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')
const { mapActions: mapReportActions, mapGetters: mapReportGetters, mapState: mapReportState } = createNamespacedHelpers('report')

export default {
  name: 'ShareForm',
  data: () => ({
    roleOptions: [
      {
        text: 'Viewer',
        value: 'viewer',
        disabled: false,
      },
      {
        text: 'Editor',
        value: 'editor',
        disabled: false,
      },
      {
        text: 'Owner',
        value: 'owner',
        disabled: false,
      },
    ],
    sharedRole: 'editor',
    sharedWith: null,
    sharedWithInputOptions: [
      { text: 'OAO Internals', value: 'internals' },
    ],
  }),

  computed: {
    ...mapAuthGetters([
      'inDevMode',
    ]),
    ...mapLayoutGetters([
      'iceColor',
      'midColor',
    ]),
    ...mapReportGetters([
      'hasEditorRights',
      'hasOwnerRights',
    ]),
    ...mapReportState({
      $roleOptions: (state) => state.roleOptions,
      $selectedReport: (state) => state.selectedReport,
      $showShareFormInputs: (state) => state.showShareFormInputs,
    }),
    ...mapToolsGetters([
      'globalTeamReports',
      'reportOptionsForSelectedTeam',
    ]),

    highlightColor() {
      return this.iceColor
    },
    secondaryColor() {
      return this.midColor
    },

    selectedReport: {
      get() {
        return this.$selectedReport
      },
      set(newValue) {
        this.$store.commit('report/selectedReport', newValue)
      },
    },
    isProtectedReport() {
      return this.selectedReport && this.selectedReport.isProtected
    },

    selectedReportRoles() {
      return this.selectedReport.roles
    },
    visibleRoles() {
      return this.selectedReportRoles.filter((role) => !role.isDeleted)
    },

    showShareFormInputs: {
      get() {
        return this.$showShareFormInputs
      },
      set(value) {
        this.$store.commit('report/showShareFormInputs', value)
      },
    },

    rulesRequired() {
      return (query) => {
        const { label, baseReportName } = query
        return [
          (value) => {
            const isStillEditing = !label && !baseReportName
            const isValidValue = isStillEditing || !!value || [0, false].indexOf(value) !== -1
            return isValidValue || 'Required'
          },
        ]
      }
    },
    rulesSharedWith() {
      const characterLimit = 250
      const isValidEmail = (value) => /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value)
      const isListOfValidEmails = (value) => {
        const isList = typeof value === 'string' && value.indexOf(',') !== -1
        const emails = isList ? value.split(',').map((str) => str.trim()) : [value]
        return emails.every((email) => isValidEmail(email))
      }
      const isValidUserGroup = (value) => {
        const userGroups = ['internals']
        return userGroups.indexOf(value) !== -1
      }

      const required = (value) => !this.showShareFormInputs || !!value || 'Required'
      const structured = (value) => !value || (typeof value === 'string' && (isValidEmail(value) || isListOfValidEmails(value) || isValidUserGroup(value))) || 'Invalid Email'
      const withinCharacterLimits = (value) => !value || (typeof value === 'string' && value.length <= characterLimit) || 'Too long'

      return [required, structured, withinCharacterLimits]
    },
  },

  watch: {
    reportOptionsForSelectedTeam: {
      deep: true,
      handler(newValue) {
        const { reportPath = 'all-data' } = this.$route.query
        this.selectedReport = newValue && newValue.find((report) => report.reportPath === reportPath || report.docId === reportPath)
      },
    },
  },

  methods: {
    ...mapReportActions(['updateReport']),

    toggleModal(modalName, modalData) {
      const storeName = 'report'
      this.$store.dispatch('tools/toggleModal', { modalData, modalName, storeName })
    },

    onCopySuccess() {
      this.copied = true

      const message = 'Copied'
      const status = 'success'

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', { message, status }, { root: true })
    },
    onCopyError(errorMessage) {
      this.copied = false

      const message = errorMessage || 'Error copying'
      const status = 'error'

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', { message, status }, { root: true })
    },
    copyLinkToClipboard() {
      const error = () => !navigator.clipboard && this.onCopyError('The Clipboard API is not available')
      const success = () => navigator.clipboard.writeText(this.selectedReport.hrefShare).then(this.onCopySuccess).catch(this.onCopyError)

      return error() || success()
    },

    revealShareFormInputs(event) {
      const isComposing = () => event.isComposing || event.keyCode === 229

      if (event && !isComposing()) {
        this.showShareFormInputs = true
      }
    },
    clearShareFormInputs() {
      this.$refs.formShare.reset()
      this.showShareFormInputs = false
      this.sharedWith = null
      this.sharedRole = 'editor'
    },

    errorForm(message) {
      const status = 'error'

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', { message, status }, { root: true })

      return true
    },
    shareReport(selectedReport) {
      const error = () => {
        const isValidForm = this.$refs.formShare.validate()
        return !isValidForm && this.errorForm('Review share form for errors.')
      }
      const success = () => {
        const { globalTeamReports, sharedWith, sharedRole } = this
        const { docIdOnParent, roles, shares } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const alreadyShared = shares.indexOf(sharedWith) !== -1
        const foundRole = roles && roles.find((role) => role.userId === sharedWith)

        if (!alreadyShared) {
          sharedReport.shares.push(sharedWith)
        }

        if (foundRole && foundRole.role !== sharedRole) {
          foundRole.role = sharedRole
          if (foundRole.isDeleted) foundRole.isDeleted = false
          if (foundRole.isRevoked) foundRole.isRevoked = false
        } else if (!foundRole) {
          const newProperties = {
            isFavorite: true,
            role: sharedRole,
          }
          const newRole = createReportRole(newProperties, sharedWith)

          if (!sharedReport.roles) {
            sharedReport.roles = [newRole]
          } else {
            sharedReport.roles.push(newRole)
          }
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
    updateSharedUserRole(selectedReport, sharedUser, role) {
      const error = () => !this.hasEditorRights && this.errorForm('Action is not allowed')
      const success = () => {
        const { globalTeamReports } = this
        const { docIdOnParent } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const { roles } = sharedReport

        const sharedWith = sharedUser.userId
        const alreadyShared = selectedReport.shares.indexOf(sharedWith) !== -1
        const foundRole = roles && roles.find((sharedRole) => sharedRole.userId === sharedWith)

        if (!alreadyShared) {
          sharedReport.shares.push(sharedWith)
        }

        if (foundRole) {
          foundRole.role = role

          if (foundRole.isDeleted) foundRole.isDeleted = false
          if (foundRole.isRevoked) foundRole.isRevoked = false
        } else {
          const newRole = {
            isArchived: false,
            isDeleted: false,
            isFavorite: true,
            isMetric: false,
            isRevoked: false,
            role,
            userId: sharedWith,
          }

          if (!sharedReport.roles) {
            sharedReport.roles = [newRole]
          } else {
            sharedReport.roles.push(newRole)
          }
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
    revokeAccessToReport(selectedReport, sharedUser) {
      const error = () => !this.hasEditorRights && this.errorForm('Action is not allowed')
      const success = () => {
        const { globalTeamReports } = this
        const { docIdOnParent } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const { shares, roles } = sharedReport
        const sharedWith = sharedUser.userId
        const foundRole = roles && roles.find((sharedRole) => sharedRole.userId === sharedWith)
        const indexOfSharedWith = shares.indexOf(sharedWith)

        if (indexOfSharedWith !== -1) {
          shares.splice(indexOfSharedWith, 1)
        }

        if (foundRole) {
          foundRole.isRevoked = true
        } else {
          const newRole = {
            isArchived: false,
            isDeleted: false,
            isFavorite: false,
            isMetric: false,
            isRevoked: true,
            role: sharedReport.defaultRole,
            userId: sharedWith,
          }

          if (!sharedReport.roles) {
            sharedReport.roles = [newRole]
          } else {
            sharedReport.roles.push(newRole)
          }
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
    restoreAccessToReport(selectedReport, sharedUser) {
      const error = () => !this.hasEditorRights && this.errorForm('Action is not allowed')
      const success = () => {
        const { globalTeamReports } = this
        const { docIdOnParent } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const { shares, roles } = sharedReport
        const sharedWith = sharedUser.userId
        const foundRole = roles && roles.find((sharedRole) => sharedRole.userId === sharedWith)
        const indexOfSharedWith = shares.indexOf(sharedWith)

        if (indexOfSharedWith === -1) {
          shares.push(sharedWith)
        }

        if (foundRole) {
          foundRole.isDeleted = false
          foundRole.isRevoked = false
        } else {
          const newRole = {
            isArchived: false,
            isDeleted: false,
            isFavorite: false,
            isMetric: false,
            isRevoked: false,
            role: sharedReport.defaultRole,
            userId: sharedWith,
          }

          if (!sharedReport.roles) {
            sharedReport.roles = [newRole]
          } else {
            sharedReport.roles.push(newRole)
          }
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
    softDeleteAccessToReport(selectedReport, sharedUser) {
      const error = () => !this.hasEditorRights && this.errorForm('Action is not allowed')
      const success = () => {
        const { globalTeamReports } = this
        const { docIdOnParent } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const sharedWith = sharedUser.userId
        const { shares, roles } = sharedReport
        const foundRole = roles && roles.find((sharedRole) => sharedRole.userId === sharedWith)
        const indexOfSharedWith = shares.indexOf(sharedWith)

        if (indexOfSharedWith !== -1) {
          shares.splice(indexOfSharedWith, 1)
        }

        if (foundRole) {
          foundRole.isDeleted = true
          foundRole.isRevoked = true
        } else {
          const newRole = {
            isArchived: false,
            isDeleted: true,
            isFavorite: false,
            isMetric: false,
            isRevoked: true,
            role: selectedReport.defaultRole,
            userId: sharedWith,
          }

          if (!sharedReport.roles) {
            sharedReport.roles = [newRole]
          } else {
            sharedReport.roles.push(newRole)
          }
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
    hardDeleteAccessToReport(selectedReport, sharedUser) {
      const error = () => !this.hasEditorRights && this.errorForm('Action is not allowed')
      const success = () => {
        const { globalTeamReports } = this
        const { docIdOnParent } = selectedReport
        const sharedReport = docIdOnParent ? globalTeamReports.find((report) => report.docId === docIdOnParent) : selectedReport
        const sharedWith = sharedUser.userId
        const { shares, roles } = sharedReport
        const foundRole = roles && roles.find((sharedRole) => sharedRole.userId === sharedWith)
        const indexOfSharedWith = shares.indexOf(sharedWith)

        if (indexOfSharedWith !== -1) {
          sharedReport.shares.splice(indexOfSharedWith, 1)
        }

        if (foundRole) {
          sharedReport.roles.splice(roles.indexOf(foundRole), 1)
        }

        return this.updateReport(sharedReport)
          .then(() => {
            this.showShareFormInputs = false
            this.sharedWith = null
            this.sharedRole = 'editor'
          })
      }

      return error() || success()
    },
  },
}
</script>
